/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { cssMap, cx, jsx } from '@compiled/react';
import invariant from 'tiny-invariant';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';
import InlineEdit, { type InlineEditProps } from '@atlaskit/inline-edit';
import { Box, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import { CUSTOM_TEXT_FIELD_ID } from '../common/constants';
import { useValidateRequiredField } from '../hooks/useValidateRequiredField';

import { CustomTooltip } from './CustomTooltip';
import { ValidationPopup } from './ValidationPopup';

const styles = cssMap({
	standardHeight: {
		height: '40px',
	},
	fullWidthHeight: {
		minHeight: '40px',
	},
	editableTextFieldContainer: {
		width: '100%',
		// The TextField component has a default 8px margin top, so we are negating this here
		marginTop: token('space.negative.100'),
	},
	fullWidthContainer: {
		marginLeft: token('space.negative.075'),
	},
	textField: {
		width: '100%',
	},
	headingEditView: {
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > [data-ds--text-field--input]': {
			font: token('font.heading.medium'),
		},
	},
	headingFullWidthEditView: {
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> textarea': {
			font: token('font.heading.medium'),
			color: token('color.text'),
			overflowWrap: 'break-word',
			overflowX: 'hidden',
		},
	},

	readViewBase: {
		width: '100%',
		alignItems: 'center',
		display: 'flex',
	},
	standardReadView: {
		height: '40px',
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
	},
	fullWidthReadView: {
		minHeight: '38px',
		paddingRight: token('space.050'),
		paddingLeft: token('space.050'),
		whiteSpace: 'pre-line',
	},
	editablePointer: {
		cursor: 'pointer',
	},
	editableReadViewHeight: {
		height: '36px', // 40px - 2 * 2px outline
	},
	editableReadViewMinHeight: {
		minHeight: '30px',
	},
});

const ReadView = memo(
	({
		text,
		isEditable,
		as = 'default',
		isFullWidth = false,
	}: {
		as?: 'heading' | 'default';
		isEditable?: boolean;
		isFullWidth?: boolean;
		text: string;
	}) => {
		const textRef = useRef<HTMLDivElement | null>(null);

		const canAppear = useCallback(() => {
			const element = textRef.current;
			invariant(element);
			// Only showing the tooltip for this item when
			// the element has been clamped.
			return element.scrollHeight > element.clientHeight;
		}, []);

		return (
			<Tooltip
				component={CustomTooltip}
				content={text}
				// don't need a screen reader announcement as the
				// tooltip content is the same as the items content
				isScreenReaderAnnouncementDisabled
				canAppear={canAppear}
			>
				{(props) => (
					// eslint-disable-next-line react/jsx-props-no-spreading
					<Box
						{...props}
						xcss={cx(
							styles.readViewBase,
							isFullWidth ? styles.fullWidthReadView : styles.standardReadView,
							isEditable && styles.editablePointer,
							isEditable && !isFullWidth && styles.editableReadViewHeight,
							isEditable && isFullWidth && as !== 'heading' && styles.editableReadViewMinHeight,
						)}
					>
						{as === 'heading' ? (
							<Heading ref={textRef} size="medium">
								{text}
							</Heading>
						) : (
							<Text ref={textRef} maxLines={isFullWidth ? undefined : 1}>
								{text}
							</Text>
						)}
					</Box>
				)}
			</Tooltip>
		);
	},
);

const FIELD_TYPE_FOR_ANALYTICS = 'TextField';

export type CustomTextFieldProps = {
	/** Renders the read view as a heading or default text. Defaults to 'default'. */
	as?: 'heading' | 'default';
	currentValue: string;
	fieldName: string;
	/** HTML input type (e.g. 'text', 'url'). Defaults to 'text'. */
	inputType?: 'text' | 'url';
	isEditable: boolean;
	isFullWidth?: boolean;
	isRequired: boolean;
	onChange: (newValue: string | null) => void;
};

const createEditView =
	(
		inputType: CustomTextFieldProps['inputType'],
		as: CustomTextFieldProps['as'],
		isFullWidth: CustomTextFieldProps['isFullWidth'],
		onConfirm: (newValue: string) => void,
	): InlineEditProps<string>['editView'] =>
	({ errorMessage, isInvalid, ...fieldProps }) => {
		const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (as === 'heading' && e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				onConfirm(e.currentTarget.value);
			}
		}, []);

		return (
			<ValidationPopup
				errorMessage={errorMessage}
				showInvalidPopup={Boolean(isInvalid && errorMessage)}
				trigger={({ ref, ...triggerProps }) =>
					isFullWidth ? (
						<Box xcss={as === 'heading' && styles.headingFullWidthEditView}>
							<TextArea
								{...fieldProps}
								onChange={(e) => fieldProps.onChange(e.currentTarget.value)}
								onKeyDown={handleKeyDown}
								autoFocus
								ref={ref}
								isInvalid={isInvalid}
								aria-invalid={isInvalid || undefined}
								aria-describedby={isInvalid ? triggerProps.errorId : undefined}
								aria-errormessage={isInvalid ? errorMessage : undefined}
								minimumRows={1}
							/>
						</Box>
					) : (
						<Textfield
							css={[styles.textField, as === 'heading' && styles.headingEditView]}
							{...fieldProps}
							type={inputType ?? 'text'}
							autoFocus
							ref={ref}
							isInvalid={isInvalid}
							aria-invalid={isInvalid || undefined}
							aria-describedby={isInvalid ? triggerProps.errorId : undefined}
							aria-errormessage={isInvalid ? errorMessage : undefined}
						/>
					)
				}
			/>
		);
	};

export const CustomTextField = ({
	as = 'default',
	isEditable,
	isFullWidth = false,
	isRequired,
	currentValue,
	fieldName,
	onChange,
	inputType = 'text',
}: CustomTextFieldProps): JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isEditing, setIsEditing] = useState(false);

	const validate = useValidateRequiredField({
		isRequired,
		isEditable,
		fieldName,
		fieldTypeForAnalytics: FIELD_TYPE_FOR_ANALYTICS,
	});

	const onEdit = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'initiated',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName,
				fieldType: FIELD_TYPE_FOR_ANALYTICS,
				isRequired,
				isEditable,
			},
		);
		setIsEditing(true);
	}, [createAnalyticsEvent, fieldName, isRequired, isEditable]);

	const onCancel = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'cancelled',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName,
				fieldType: FIELD_TYPE_FOR_ANALYTICS,
				isRequired,
				isEditable,
			},
		);
		setIsEditing(false);
	}, [createAnalyticsEvent, fieldName, isRequired, isEditable]);

	const onConfirm = useCallback(
		(newValue: string) => {
			setIsEditing(false);
			if (newValue !== currentValue) {
				onChange(newValue);
			}
		},
		[onChange, currentValue],
	);

	const EditView = useMemo(
		() => createEditView(inputType, as, isFullWidth, onConfirm),
		[inputType, as, isFullWidth, onConfirm],
	);

	// Creating components on render sucks
	// Ideally the values for text and isEditable would be passed as props to the ReadView component
	// But the atlaskit InlineEdit component doesn't support passing props to the read view component
	// Hence we do the bare minimum of using useCallback to memoize the ReadView component
	const ReadViewComponent = useCallback(() => {
		return (
			<ReadView text={currentValue} isEditable={isEditable} as={as} isFullWidth={isFullWidth} />
		);
	}, [currentValue, isEditable, as, isFullWidth]);

	if (!isEditable) {
		return <ReadViewComponent />;
	}

	return (
		<Box
			data-attr={CUSTOM_TEXT_FIELD_ID}
			xcss={cx(
				styles.editableTextFieldContainer,
				isFullWidth && styles.fullWidthContainer,
				isFullWidth ? styles.fullWidthHeight : styles.standardHeight,
			)}
		>
			<InlineEdit
				isEditing={isEditing}
				defaultValue={currentValue}
				editButtonLabel={currentValue}
				editView={EditView}
				readView={ReadViewComponent}
				onConfirm={onConfirm}
				onCancel={onCancel}
				onEdit={onEdit}
				hideActionButtons={false}
				readViewFitContainerWidth
				validate={validate}
			/>
		</Box>
	);
};
